import { action, asyncAction } from '@play-co/replicant';
import { TeamProfile } from './team.schema';
import createActions from '../config/actions/createActions';
import {
  getSharedStateActionsAPI,
  getSharedStateAsyncActionsAPI,
  getUserSharedState,
} from '../config/actions/createSharedStatesActions';
import { getRealLength } from '../utils/string';
import {
  EmoteID,
  TextMessageID,
  MAX_MEMBERS,
  TEAM_CREATION_COST,
  TeamRole,
} from './team.ruleset';
import * as Inventory from '../inventory';
import { AnalyticsEvent, Feature, sendAnalyticsEvents } from '../analytics';
import {
  getCurrentTeamEventId,
  getCurrentTeamEventType,
  getPreviousTeamEventId,
  getTeamEventScore,
} from './team.getters';
import type { getTeamEventAnalytics } from './competition/teamCompetition.getters';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import { joinTeamCompetitionEvent } from './competition/teamCompetition.modifiers';
import { assertNever } from '../utils';
import { joinTeamChestEvent } from './chest/teamChest.modifiers';

export type TeamChatMessage =
  | {
      type: 'emote';
      id: EmoteID;
    }
  | {
      type: 'text';
      id: TextMessageID;
    }
  | {
      type: 'energyRequest';
      value: number;
    };

export const teamActions = createActions({
  asyncCreateTeam: asyncAction(async (state, args: TeamProfile, rawApi) => {
    const api = getSharedStateAsyncActionsAPI(rawApi);

    // Validate team profile.
    if (getRealLength(args.name) < 3 || getRealLength(args.name) > 20) {
      throw new Error('Team name must be between 3 and 20 characters.');
    }
    if (getRealLength(args.description) > 50) {
      throw new Error('Team description must be shorter than 50 characters.');
    }

    // Check if user has enough gems to create a team.
    if (Inventory.getters.getGems(state) < TEAM_CREATION_COST) {
      throw new Error('User does not have enough gems to create a team.');
    }

    // Keep track of previous currencies.
    const now = api.date.now();

    // Consume gems.
    Inventory.modifiers.consumeGems(
      state,
      TEAM_CREATION_COST,
      {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.CREATE,
      },
      api,
    );

    // Leave current team if any.
    if (state.team.currentTeamId) {
      api.sharedStates.team.postMessage.removeMember(state.team.currentTeamId, {
        timestamp: now,
      });
      delete state.team.currentTeamId;
    }

    // Create new team.
    const teamId = api.sharedStates.team.create();
    state.team.currentTeamId = teamId;
    api.sharedStates.team.postMessage.joinTeam(teamId, { timestamp: now });
    api.sharedStates.team.postMessage.updateTeamProfile(teamId, {
      ...args,
      timestamp: now,
    });

    // Auto care requests are on by default
    state.team.autoCareRequest = true;

    return teamId;
  }),

  asyncJoinTeam: asyncAction(
    async (state, args: { teamId: string }, rawApi) => {
      const api = getSharedStateAsyncActionsAPI(rawApi);
      const now = api.date.now();

      // Fetch team state.
      let teamState = await api.sharedStates.team.fetch(args.teamId);

      // Team does not exist.
      if (!teamState) throw new Error('Team does not exist.');

      // Check if user has enough level to join the team.
      if (teamState?.global.profile.levelRequired > state.match2.level) {
        return { error: 'levelTooLow' } as const;
      }

      // Check if team is full.
      const memberCount = Object.keys(teamState.global.members).length;
      if (memberCount >= MAX_MEMBERS) {
        return { error: 'teamFull' } as const;
      }
      // Join new team.
      api.sharedStates.team.postMessage.joinTeam(args.teamId, {
        timestamp: now,
      });

      // Flush messages.
      await api.flushMessages();

      // Get updated team state.
      teamState = await api.sharedStates.team.fetch(args.teamId);

      // Check if joined team successfully.
      // This can happen if two users join the same team at the same time and the team becomes full.
      if (!teamState?.global.members[state.id]) {
        return { error: 'tryAgain' } as const;
      }

      // Leave current team if any.
      if (state.team.currentTeamId) {
        api.sharedStates.team.postMessage.removeMember(
          state.team.currentTeamId,
          {
            timestamp: now,
          },
        );
        delete state.team.currentTeamId;
      }

      // Update current team id.
      state.team.currentTeamId = args.teamId;

      // Auto care requests are on by default
      state.team.autoCareRequest = true;

      return {
        id: args.teamId,
        memberCount: memberCount + 1,
        ownerPlayerId: Object.keys(teamState.global.members).find(
          (x) => teamState!.global.members[x]?.role === TeamRole.Admin,
        ),
        previousScore: getTeamEventScore(
          teamState,
          getPreviousTeamEventId(teamState, now),
        ),
      };
    },
  ),

  leaveTeam: action((state, _: void, rawApi) => {
    const teamId = state.team.currentTeamId;

    // User is not in the team.
    if (!teamId) return;

    const api = getSharedStateActionsAPI(rawApi);

    api.sharedStates.team.postMessage.removeMember(teamId, {
      timestamp: api.date.now(),
    });
    if (getUserSharedState(state).team[teamId]) {
      api.sharedStates.team.deleteUserState(teamId);
    }

    delete state.team.currentTeamId;
  }),

  updateTeamProfile: action((state, args: TeamProfile, rawApi) => {
    const api = getSharedStateActionsAPI(rawApi);
    if (!state.team.currentTeamId) return;

    api.sharedStates.team.postMessage.updateTeamProfile(
      state.team.currentTeamId,
      {
        ...args,
        timestamp: api.date.now(),
      },
    );
  }),

  asyncKickMember: asyncAction(
    async (state, { userId }: { userId: string }, rawApi) => {
      const api = getSharedStateAsyncActionsAPI(rawApi);

      // User is not in a team.
      if (!state.team.currentTeamId) return;

      // Only admins can kick members.
      const teamState = await api.sharedStates.team.fetch(
        state.team.currentTeamId,
      );
      if (teamState?.global.members[state.id].role !== 'admin') {
        throw new Error('Only admins can kick members.');
      }

      // Kick member.
      api.postMessage.kickFromTeam(userId, {});
      api.sharedStates.team.postMessage.removeMember(state.team.currentTeamId, {
        timestamp: api.date.now(),
        userId,
      });

      await api.flushMessages();
    },
  ),

  sendMessageToTeam: action((state, args: TeamChatMessage, rawApi) => {
    if (!state.team.currentTeamId) return 0;

    const now = rawApi.date.now();
    if (args.type === 'energyRequest') {
      state.team.lastEnergyRequestTimestamp = now;
    }

    const api = getSharedStateActionsAPI(rawApi);

    api.sharedStates.team.postMessage.sendChatMessageToTeam(
      state.team.currentTeamId,
      {
        timestamp: now,
        contents: args,
      },
    );

    return now;
  }),

  asyncJoinTeamEvent: asyncAction(
    async (
      state,
      { teamId }: { teamId?: string } = {},
      rawApi,
    ): Promise<false | ReturnType<typeof getTeamEventAnalytics>> => {
      const api = getSharedStateAsyncActionsAPI(rawApi);

      // User is not in the team.
      teamId = teamId ?? state.team.currentTeamId;
      if (!teamId) return false;

      // Clear expired team events.
      const now = api.date.now();
      api.sharedStates.team.postMessage.clearExpiredEvents(teamId, {
        now,
      });

      // No event to join.
      const eventId = getCurrentTeamEventId(now);
      if (!eventId) return false;

      // Join event based on type.
      const eventType = getCurrentTeamEventType(now)!;
      switch (eventType) {
        case 'chest':
          return joinTeamChestEvent(teamId, api);
        case 'competition':
          return joinTeamCompetitionEvent(teamId, api);
        default:
          assertNever(eventType);
      }
    },
  ),

  asyncClaimTeamEventRewards: asyncAction(async (state, _: void, rawApi) => {
    const api = getSharedStateAsyncActionsAPI(rawApi);

    // User is not in the team.
    const teamId = state.team.currentTeamId;
    if (!teamId) {
      throw new Error(
        'Cannot claim team event rewards, user is not in a team.',
      );
    }

    // Figure out which rewards to claim.
    const unclaimedRewards = await api.asyncGetters.getUnclaimedTeamEvents();

    // Keep track of previous currencies.
    const now = api.date.now();

    // Grant all rewards.
    const events: AnalyticsEvent[] = [];

    unclaimedRewards?.forEach((reward) => {
      if (!reward.rewards) return;
      grantMatch2Rewards(
        state,
        reward.rewards,
        {
          feature: Feature.MATCH2.TEAM._,
          $subFeature:
            reward.type === 'chest'
              ? Feature.MATCH2.TEAM.CHEST.EVENT_REWARDS
              : Feature.MATCH2.TEAM.EVENT_REWARDS,
        },
        api,
      );
    });

    sendAnalyticsEvents(api, events);

    // Claim rewards on team state.
    api.sharedStates.team.postMessage.claimRewards(teamId, { timestamp: now });

    // Already sent analytics.
  }),

  setTeamAutocareRequest: action((state, autoRequest: boolean, _) => {
    // Not in a team, no reason to change this setting
    if (!state.team.currentTeamId) return;

    state.team.autoCareRequest = autoRequest;
  }),

  sendEnergyToTeamMember: action((state, targetId: string, rawApi) => {
    // User is not in a team.
    const teamId = state.team.currentTeamId;
    if (!teamId) {
      return false;
    }

    const api = getSharedStateActionsAPI(rawApi);

    const timestamp = api.date.now();
    api.postMessage.sendEnergy(targetId, { timestamp });
    api.sharedStates.team.postMessage.sendEnergyHelp(teamId, {
      targetId,
      timestamp,
    });

    return true;
  }),

  setLastCompetitionId: action((state, _: void, api) => {
    state.team.lastCompetitionEventId = getCurrentTeamEventId(api.date.now());
    state.team.seenCompetitionInfo = true;
  }),

  blockUser: action((state, { userId }: { userId: string }, rawApi) => {
    const api = getSharedStateActionsAPI(rawApi);
    const teamId = state.team.currentTeamId;
    if (!teamId) {
      throw new Error('Cannot block user, user is not in a team.');
    }

    const blockedUsers =
      getUserSharedState(state).team[teamId]?.blockedUsers ?? [];

    api.sharedStates.team.setUserState(teamId, {
      blockedUsers: [...blockedUsers, userId],
    });
  }),

  unblockUser: action((state, { userId }: { userId: string }, rawApi) => {
    const api = getSharedStateActionsAPI(rawApi);
    const teamId = state.team.currentTeamId;
    if (!teamId) {
      throw new Error('Cannot unblock user, user is not in a team.');
    }

    const blockedUsers =
      getUserSharedState(state).team[teamId]?.blockedUsers ?? [];

    api.sharedStates.team.setUserState(teamId, {
      blockedUsers: blockedUsers.filter((u) => u !== userId),
    });
  }),

  updateLastSeenTeamMessageTimestamp: action((state, _: void, api) => {
    state.team.lastSeenMessageTimestamp = api.date.now();
  }),
});
